// src/router.ts
// @ts-nocheck
// @vite-ignore

import { renderSearchResults } from "./pages/SearchResults";
import { renderImageSearchResults } from "./pages/ImageSearchResults";
import { renderNBARoute } from "./pages/nbaRoute";
import { renderHome } from "./pages/Home";
import { renderIncognitoPage } from "./pages/IncognitoPage";
import { renderDownloadAyeSearch } from "./pages/DownloadAyeSearch";

// =============================
// DATA IMPORTS
// =============================

// 1. Companies
import companiesData from "./data/companies.json";

// 2. Celebrities
import celebritiesData from "./data/processed/celeb-actors.json";

// 3. NBA Players
import nbaPlayersData from "./data/processed/nba-unique-players.json";

// 4. Japan Municipalities
import rawJapanMunicipalities from "./data/japan-municipalities-main/japan_municipalities_final.json";
import type { Municipality } from "./types/Municipality";

const municipalities: (Municipality & { slug: string })[] = (
  rawJapanMunicipalities as Municipality[]
).map((m) => ({
  ...m,
  slug: m.name.toLowerCase().replace(/[^a-z0-9]+/g, "-"),
}));

// 5. Countries (load all 195 JSON files)
import type { Country } from "./types/Country";

const countryFiles = import.meta.glob("./data/countries/*.json", {
  eager: true,
  import: "default",
});

const countries: Country[] = Object.values(countryFiles);

// =============================
// ROUTER CORE
// =============================

type RouteHandler = (params: Record<string, string>) => void;

interface Route {
  pattern: RegExp;
  handler: RouteHandler;
}

const routes: Route[] = [];

/* ============================================================
   REGISTER A ROUTE
   ============================================================ */
export function onRoute<T extends Record<string, string>>(
  pattern: RegExp,
  handler: (params: T) => void,
) {
  routes.push({
    pattern,
    handler: handler as RouteHandler,
  });
}

/* ============================================================
   MAIN ROUTE HANDLER
   ============================================================ */
export function handleRoute() {
  const path = window.location.pathname;

  // Weather shortcut
  if (path === "/weather" || path === "/weather/") {
    window.history.replaceState({}, "", "/search?q=weather");
    renderSearchResults("weather");
    return;
  }

  // Home
  if (path === "/" || path === "") {
    renderHome();
    return;
  }

  // Images vertical MUST be checked before /search.
  if (path === "/search/images" || path === "/search/images/") {
    const params = new URLSearchParams(window.location.search);
    const q = params.get("q") || "";
    renderImageSearchResults(q);
    return;
  }

  // All/web search
  if (path === "/search" || path === "/search/") {
    const params = new URLSearchParams(window.location.search);
    const q = params.get("q") || "";
    renderSearchResults(q);
    return;
  }

  // Incognito information page
  if (path === "/incognito" || path === "/incognito/") {
    renderIncognitoPage();
    return;
  }

  // AyeSearch Browser downloads landing/update page
  if (
    path === "/downloads" ||
    path === "/downloads/" ||
    path === "/downloads/update" ||
    path === "/downloads/update/"
  ) {
    renderDownloadAyeSearch();
    return;
  }

  // Registered entity routes
  for (const route of routes) {
    const match = path.match(route.pattern);
    if (match) {
      route.handler(match.groups || {});
      return;
    }
  }

  const app = document.querySelector("#app");
  if (app) {
    app.innerHTML = `
      <div class="error-page">
        <h1>Page Not Found</h1>
      </div>
    `;
  }
}

/* ============================================================
   PROGRAMMATIC NAVIGATION
   ============================================================ */
export function navigate(path: string) {
  history.pushState({}, "", path);
  requestAnimationFrame(handleRoute);
}

/* ============================================================
   ROUTES DEFINITIONS
   ============================================================ */

// COUNTRIES — /countries/:slug
onRoute(/^\/countries\/(?<slug>[a-z0-9-]+)$/, ({ slug }) => {
  const app = document.getElementById("app");
  if (!app) return;

  const country = countries.find((c) => c.slug === slug);

  if (!country) {
    app.innerHTML = `<h1>Country Not Found</h1>`;
    return;
  }

  import("./components/countries/CountryLayout").then(
    ({ renderCountryLayout }) => {
      renderCountryLayout(country);
    },
  );
});

// COMPANIES — /companies/:slug
onRoute(/^\/companies\/(?<slug>[a-z0-9-]+)$/, ({ slug }) => {
  const app = document.getElementById("app");
  if (!app) return;

  const company = companiesData.find((c: any) => c.slug === slug);

  if (!company) {
    app.innerHTML = `<h1>Company Not Found</h1>`;
    return;
  }

  import("./pages/companies/CompanyLayout").then(
    ({ renderCompanyLayout }) => {
      renderCompanyLayout(company);
    },
  );
});

// CELEBRITIES — /celebrities/:slug
onRoute(/^\/celebrities\/(?<slug>[a-z0-9-]+)$/, ({ slug }) => {
  const app = document.getElementById("app");
  if (!app) return;

  const celeb = (celebritiesData as any[]).find(
    (c) => c.slug === slug,
  );

  if (!celeb) {
    app.innerHTML = `<h1>Celebrity Not Found</h1>`;
    return;
  }

  import("./pages/celebrities/CelebrityLayout").then(
    ({ renderCelebrityLayout }) => {
      renderCelebrityLayout(celeb);
    },
  );
});

// MUNICIPALITIES — /municipalities/:slug
onRoute(/^\/municipalities\/(?<slug>[a-z0-9-]+)$/, ({ slug }) => {
  const app = document.getElementById("app");
  if (!app) return;

  const muni = municipalities.find((m) => m.slug === slug);

  if (!muni) {
    app.innerHTML = `<h1>Municipality Not Found</h1>`;
    return;
  }

  import("./components/municipalities/MunicipalityCard").then(
    ({ renderMunicipalityCard, initMunicipalityCardEvents }) => {
      app.innerHTML = renderMunicipalityCard(muni);
      initMunicipalityCardEvents(app);
    },
  );
});

// NBA — /nba/:slug
onRoute(/^\/nba\/(?<slug>[a-z0-9-]+)$/, ({ slug }) => {
  const player = nbaPlayersData.find(
    (p: any) => p.slug === slug,
  );

  if (player) {
    renderNBARoute(player);
  } else {
    const app = document.querySelector("#app");
    if (app) {
      app.innerHTML = `<h1>NBA Player Not Found</h1>`;
    }
  }
});

/* ============================================================
   LIFECYCLE INITIALIZATION
   ============================================================ */
export function initRouter() {
  window.addEventListener("popstate", handleRoute);
  handleRoute();
}
